import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { Briefcase, Copy, FileWarning } from 'lucide-react';
import { apiRequest } from '../lib/api';
import { useToast } from '../lib/toast';
import { useAuthStore } from '../lib/auth-store';
import { ListShell } from '../components/ListShell';
import { DataTable } from '../components/ui/DataTable';
import { EmptyState } from '../components/ui/EmptyState';
import { Pagination } from '../components/people/Pagination';
import { GatedSection } from '../components/shared/GatedSection';
import { EditIconButton } from '../components/shared/EditIconButton';
import { ListOrOutlet } from '../components/shared/ListOrOutlet';
import { timeAgo } from '../lib/format';
import {
  FilterBar,
  SortableHeader,
  useListFilters,
  type FilterRoute,
} from '../components/filters';
import {
  APPLICATIONS_SORT_COLUMNS,
  ApplicationEditDrawer,
  StageChip,
  applicationsFilterConfig,
  applicationsSearchSchema,
  daysSince,
  type ApplicationListItem,
  type ApplicationListResponse,
  type CandidateApplication,
} from '../components/hiring';

const HR_ROLES: ReadonlyArray<string> = ['super_admin', 'academic_admin', 'hr'];

const PAGE_SIZE = 25;

/**
 * L-1: the list endpoint (`GET /api/hiring/applications`) attaches a
 * `documentCount` to every row so the queue can flag applicants with no
 * uploaded documents without a second request. The shared
 * `ApplicationListItem` type does not yet carry the field, so we intersect it
 * locally. Kept required (the backend always emits it, defaulting to 0); the
 * badge still uses a strict `=== 0` check so a missing value degrades to "no
 * badge" rather than a false alarm.
 */
type ApplicationRow = ApplicationListItem & { documentCount: number };

export const Route = createFileRoute('/_authed/hr/applications')({
  validateSearch: applicationsSearchSchema,
  component: HiringApplicationsPage,
});

function HiringApplicationsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const role = useAuthStore((s) => s.user?.primaryRole ?? null);
  const canEdit = role !== null && HR_ROLES.includes(role);
  const [editing, setEditing] = useState<CandidateApplication | null>(null);

  // The single source of truth for search / filters / sort / page, backed by
  // the route's URL search params. The adapter narrows the route's precisely-
  // typed search/navigate surface to the toolkit's structural `FilterRoute`
  // contract; the route's own Zod `validateSearch` keeps the boundary typed.
  const filterRoute: FilterRoute = {
    useSearch: () => Route.useSearch() as Record<string, unknown>,
    useNavigate: () => {
      const navigateRoute = Route.useNavigate();
      return (opts) =>
        navigateRoute({
          search: ((prev: Record<string, unknown>) => opts.search(prev)) as never,
          ...(opts.replace !== undefined ? { replace: opts.replace } : {}),
        });
    },
  };
  const filters = useListFilters(applicationsFilterConfig, filterRoute, {
    pageSize: PAGE_SIZE,
  });
  const { values, apiParams, setSort, setPage } = filters;

  // ONE query, driven by the toolkit's `apiParams`. The default `pipelineStage`
  // (`ACTIVE_STAGE_DEFAULTS`) reproduces the old "Active only" view; clearing
  // the Stage facet drops the param and the backend returns every stage — the
  // old "Include archived" toggle. `pipelineStage` travels comma-joined (the
  // bar's format), which the backend accepts, so the old repeated-param append
  // loop is gone.
  const listQuery = useQuery({
    queryKey: ['hiring', 'applications', apiParams] as const,
    queryFn: ({ signal }) =>
      apiRequest<ApplicationListResponse>('/api/hiring/applications', {
        signal,
        query: apiParams,
      }),
    placeholderData: (prev) => prev,
  });

  const columns = useMemo<ColumnDef<ApplicationRow, unknown>[]>(
    () => {
      const cols: ColumnDef<ApplicationRow, unknown>[] = [
      {
        id: 'fullName',
        enableSorting: false,
        header: () => (
          <SortableHeader
            label="Candidate"
            binding={APPLICATIONS_SORT_COLUMNS.fullName}
            currentSort={values.sort}
            onSort={setSort}
          />
        ),
        cell: (info) => (
          <div className="min-w-0">
            <div className="flex min-w-0 items-center gap-2">
              <p className="truncate font-medium text-[color:var(--color-text)]">
                {info.row.original.fullName}
              </p>
              {info.row.original.documentCount === 0 ? (
                <span
                  className="inline-flex shrink-0 items-center gap-1 rounded-full border border-[color:var(--color-warning)]/40 bg-[color:var(--color-warning)]/10 px-2 py-0.5 text-[11px] font-medium text-[color:var(--color-warning)]"
                  title="No documents uploaded yet — chase the candidate for certificates and ID."
                >
                  <FileWarning size={11} aria-hidden="true" />
                  No docs
                </span>
              ) : null}
            </div>
            <p className="truncate text-xs text-[color:var(--color-text-muted)]">
              {info.row.original.email}
            </p>
          </div>
        ),
      },
      {
        id: 'contact',
        enableSorting: false,
        header: 'Phone',
        cell: (info) => (
          <CopyableText
            value={info.row.original.phoneNumber}
            label="Copy phone"
          />
        ),
      },
      {
        id: 'courses',
        enableSorting: false,
        header: 'Courses preferred',
        cell: (info) => {
          const courses = info.row.original.coursesPreferred ?? [];
          if (courses.length === 0) return <span className="text-xs text-[color:var(--color-text-subtle)]">—</span>;
          return (
            <div className="flex flex-wrap gap-1">
              {courses.slice(0, 3).map((c) => (
                <span
                  key={c}
                  className="rounded-full border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-2 py-0.5 text-[10px] uppercase tracking-wide text-[color:var(--color-text-muted)]"
                >
                  {c}
                </span>
              ))}
              {courses.length > 3 ? (
                <span className="text-[10px] text-[color:var(--color-text-subtle)]">
                  +{courses.length - 3}
                </span>
              ) : null}
            </div>
          );
        },
      },
      {
        id: 'stage',
        enableSorting: false,
        header: 'Stage',
        cell: (info) => (
          <StageChip stage={info.row.original.pipelineStage} size="md" />
        ),
      },
      {
        id: 'daysInStage',
        enableSorting: false,
        header: 'Days in stage',
        cell: (info) => {
          const days = daysSince(
            info.row.original.updatedAt ?? info.row.original.submittedAt,
          );
          return (
            <span
              className={
                days >= 7
                  ? 'text-sm font-semibold text-[color:var(--color-warning)] tabular-nums'
                  : 'text-sm text-[color:var(--color-text-muted)] tabular-nums'
              }
            >
              {days}d
            </span>
          );
        },
      },
      {
        id: 'submitted',
        enableSorting: false,
        header: () => (
          <SortableHeader
            label="Submitted"
            binding={APPLICATIONS_SORT_COLUMNS.submittedAt}
            currentSort={values.sort}
            onSort={setSort}
          />
        ),
        cell: (info) => (
          <span className="text-xs text-[color:var(--color-text-muted)] tabular-nums">
            {timeAgo(info.row.original.submittedAt)}
          </span>
        ),
      },
      ];

      if (canEdit) {
        cols.push({
          id: 'edit',
          enableSorting: false,
          header: () => <span className="sr-only">Edit</span>,
          cell: (info) => (
            <div className="flex justify-end">
              <EditIconButton
                label={`Edit ${info.row.original.fullName}`}
                onClick={() => setEditing(info.row.original)}
              />
            </div>
          ),
        });
      }

      return cols;
    },
    [canEdit, values.sort, setSort],
  );

  const items = (listQuery.data?.items ?? []) as ApplicationRow[];
  const total = listQuery.data?.total ?? 0;
  const isLoadingList = listQuery.isLoading && !listQuery.data;

  return (
    <ListOrOutlet basePath="/hr/applications">
    <ListShell
      title="Hiring applications"
      description="Every prospective teacher in the pipeline. Click a row to open the full record."
    >
      <FilterBar
        config={applicationsFilterConfig}
        filters={filters}
        resultCount={total}
      />

      <GatedSection
        error={listQuery.error}
        title="Hiring"
        onRetry={() => listQuery.refetch()}
      >
        <DataTable<ApplicationRow>
          data={items}
          columns={columns}
          isLoading={isLoadingList}
          getRowId={(row) => row.id}
          onRowClick={(row) =>
            navigate({
              to: '/hr/applications/$applicationId',
              params: { applicationId: row.id },
            })
          }
          emptyState={
            <EmptyState
              icon={<Briefcase size={28} aria-hidden="true" />}
              title="No applications match these filters"
              description="Try clearing a filter, or share the careers page link with prospective teachers. New applications appear here automatically."
            />
          }
        />
        <Pagination
          page={values.page}
          pageSize={PAGE_SIZE}
          total={total}
          onPageChange={setPage}
        />
      </GatedSection>

      <ApplicationEditDrawer
        open={editing !== null}
        application={editing}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          void queryClient.invalidateQueries({
            queryKey: ['hiring', 'applications'],
          });
          if (editing) {
            void queryClient.invalidateQueries({
              queryKey: ['hiring', 'application', editing.id],
            });
          }
        }}
      />
    </ListShell>
    </ListOrOutlet>
  );
}

function CopyableText({ value, label }: { value: string; label: string }) {
  const { toast } = useToast();
  if (!value) return <span>—</span>;
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        void navigator.clipboard
          .writeText(value)
          .then(() => toast('Copied to clipboard.', { variant: 'success' }))
          .catch(() => toast('Could not copy.', { variant: 'error' }));
      }}
      aria-label={label}
      className="inline-flex items-center gap-1 rounded-md px-1 text-xs text-[color:var(--color-text-muted)] hover:bg-[color:var(--color-surface)] hover:text-[color:var(--color-text)]"
    >
      <span className="tabular-nums">{value}</span>
      <Copy size={11} aria-hidden="true" />
    </button>
  );
}
